import { doc, getDocFromServer } from "firebase/firestore";

import { auth, db } from "./firebase";
import {
  cleanupCurrentPostImages,
  cleanupCurrentPostPdf,
  uploadPostImages,
  uploadPostPdf,
  type PendingAttachment,
  type UploadedPostImage,
} from "./postAttachments";
import { createPostDocumentViaRest, readPostAuthorUidViaRest } from "./postFirestoreRest";
import { buildPostPublishPayload } from "./postPublishPayload";
import { isPostAuthorAttribution, loadCanonicalPostAuthor } from "./postPublishing";
import {
  isIdempotentCommitConflict,
  shouldReconcileCommitError,
  withSingleTokenRefresh,
} from "./postPublishCore";
import { deletePostPublishJobFiles, durableAttachmentExists } from "./postPublishFiles";
import type { PostPublishJob, PostPublishStorageResult } from "./postPublishTypes";

const emptyJobs: PostPublishJob[] = [];

function pendingAttachment(attachment: PostPublishJob["attachments"][number]): PendingAttachment {
  if (!durableAttachmentExists(attachment.durableUri)) {
    throw Object.assign(new Error("Le fichier local est introuvable. Selectionne-le de nouveau."), { code: "post/file-not-found" });
  }
  return {
    uri: attachment.durableUri,
    type: attachment.type,
    name: attachment.name,
    mimeType: attachment.contentType,
    size: attachment.normalizedSize,
    width: attachment.width,
    height: attachment.height,
  };
}

async function cleanupAttempt(params: {
  uid: string;
  postId: string;
  images: UploadedPostImage[];
  pdf?: PostPublishStorageResult;
}) {
  await Promise.allSettled([
    params.images.length
      ? cleanupCurrentPostImages({ uid: params.uid, postId: params.postId, storagePaths: params.images.map((image) => image.storagePath) })
      : Promise.resolve(),
    cleanupCurrentPostPdf({ uid: params.uid, postId: params.postId, storagePath: params.pdf?.storagePath }),
  ]);
}

// Browser publication is deliberately online-only: enqueue resolves only after
// Storage and Firestore acknowledge the complete attempt. This keeps object URLs
// scoped to the composer while sharing Native's canonical payload and paths.
export const postPublishQueue = {
  subscribe: (_listener: () => void) => () => {},
  getSnapshot: () => emptyJobs,
  resume: () => {},
  async retry(_jobId: string) {},
  async cancel(_jobId: string) {},
  async remove(_jobId: string) {},
  async reconcileRemote(_posts: readonly { id: string; authorUid: string; moderationStatus?: string }[]) {},
  async enqueue(job: PostPublishJob) {
    const user = auth.currentUser;
    if (!user || user.uid !== job.authorUid) throw new Error("Session de publication indisponible.");
    if (!isPostAuthorAttribution(job.author) || job.author.authorUid !== job.authorUid) {
      throw new Error("Cette publication utilise une attribution publique invalide.");
    }
    const imageAttachments = job.attachments.filter((attachment) => attachment.type === "image");
    const pdfAttachment = job.attachments.find((attachment) => attachment.type === "pdf");
    if (imageAttachments.length > 6 || job.attachments.filter((attachment) => attachment.type === "pdf").length > 1) {
      throw new Error("La selection de medias depasse les limites autorisees.");
    }

    const canonicalAuthor = await loadCanonicalPostAuthor(
      user.uid,
      user.email,
      job.author.authorIdentityId
    );
    let group = job.group;
    if (job.visibility === "group" && job.groupId) {
      const snapshot = await getDocFromServer(doc(db, "groups", job.groupId));
      if (!snapshot.exists()) throw new Error("Groupe introuvable.");
      group = {
        groupId: snapshot.id,
        groupName: String(snapshot.data().name ?? ""),
        isPublic: snapshot.data().isPublic === true,
      };
    }

    let images: UploadedPostImage[] = [];
    let pdf: PostPublishStorageResult | undefined;
    let committed = false;
    try {
      if (imageAttachments.length) {
        images = await uploadPostImages({
          uid: user.uid,
          postId: job.postId,
          images: imageAttachments.map(pendingAttachment),
          normalize: false,
          storageFileNames: imageAttachments.map((attachment) => attachment.attachmentId),
        });
      }
      if (pdfAttachment) {
        const storagePath = `moderation-pending/${user.uid}/${job.postId}/${pdfAttachment.attachmentId}.pdf`;
        const result = await uploadPostPdf({
          uid: user.uid,
          attachment: pendingAttachment(pdfAttachment),
          storagePath,
        });
        pdf = {
          storagePath,
          contentType: "application/pdf",
          name: result.attachmentName,
        };
      }

      const payload = buildPostPublishPayload(job, canonicalAuthor, group, images, pdf);
      try {
        await withSingleTokenRefresh({
          getToken: (force) => user.getIdToken(force),
          operation: (token) => createPostDocumentViaRest(job.postId, payload, {
            token,
            expectedUid: user.uid,
          }),
        });
        committed = true;
      } catch (error) {
        if (isIdempotentCommitConflict(error as { code?: string; status?: number })) {
          committed = true;
        } else if (shouldReconcileCommitError(error as { code?: string; status?: number })) {
          const owner = await withSingleTokenRefresh({
            getToken: (force) => user.getIdToken(force),
            operation: (token) => readPostAuthorUidViaRest(job.postId, { token, expectedUid: user.uid }),
          }).catch(() => null);
          if (owner === user.uid) committed = true;
          else throw error;
        } else {
          throw error;
        }
      }
    } catch (error) {
      if (!committed) await cleanupAttempt({ uid: user.uid, postId: job.postId, images, pdf });
      throw error;
    } finally {
      if (committed) deletePostPublishJobFiles(job.jobId);
    }
  },
};
